import React, { useEffect, useState, useRef } from 'react';
import { supabase } from '../../config/supabase';
import { useRealtimeContacts } from '../../hooks/useRealtime';

const Enquiries = () => {
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedEnquiry, setSelectedEnquiry] = useState(null);
    const [enquiries, setEnquiries] = useState([]);
    const [lastUpdated, setLastUpdated] = useState(null);
    const [newMessage, setNewMessage] = useState(null);
    const intervalRef = useRef(null);

    // Real-time: Fetch immediately when new contact arrives
    useRealtimeContacts((data) => {
        console.log('New contact message received:', data);
        setNewMessage(data);
        fetchEnquiries(); // Refresh the list
    });

    // Initial fetch and polling backup
    useEffect(() => {
        fetchEnquiries();
        intervalRef.current = setInterval(fetchEnquiries, 10000); // 10s backup polling
        return () => clearInterval(intervalRef.current);
    }, []);

    // Fetch directly from Supabase (works even if backend is down)
    const fetchEnquiries = async () => {
        try {
            const { data, error } = await supabase
                .from('contact_messages')
                .select('*')
                .order('created_at', { ascending: false });
            
            if (error) throw error;
            
            console.log('Enquiries fetched from Supabase:', data);
            setEnquiries(data || []);
            setLastUpdated(new Date().toLocaleTimeString());
        } catch (err) {
            console.error('Failed to fetch enquiries', err);
            setEnquiries([]);
        }
    };

    const handleView = async (enq) => {
        setSelectedEnquiry(enq);

        // MARK AS READ ONLY IF NEW - directly in Supabase
        if (enq.status === 'New') {
            try {
                const { error } = await supabase
                    .from('contact_messages')
                    .update({ status: 'Read' })
                    .eq('id', enq.id);
                
                if (error) throw error;

                setEnquiries(prev =>
                    prev.map(item =>
                        item.id === enq.id
                            ? { ...item, status: 'Read' }
                            : item
                    )
                );
            } catch (err) {
                console.error('Failed to mark as read', err);
            }
        }
    };

    const filteredEnquiries = (enquiries || []).filter(enq =>
        enq?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        enq?.email?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div className="admin-page">
            <header className="admin-page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
                <h1>Incoming Enquiries</h1>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    {lastUpdated && (
                        <span style={{ fontSize: '12px', color: '#888' }}>
                            🟢 Live · Last updated: {lastUpdated}
                        </span>
                    )}
                    <button
                        onClick={fetchEnquiries}
                        style={{ padding: '6px 14px', background: '#1a237e', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' }}
                    >
                        ↻ Refresh
                    </button>
                </div>
            </header>

            <div className="admin-card">
                <div className="table-actions">
                    <input
                        type="text"
                        placeholder="Search by name or email..."
                        className="search-input"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>

                <div className="table-responsive">
                    <table className="data-table">
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Email</th>
                                <th>Subject</th>
                                <th>Message</th>
                                <th>Date</th>
                                <th>Status</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredEnquiries.map(enq => (
                                <tr key={enq.id}>
                                    <td data-label="Name">{enq.name}</td>
                                    <td data-label="Email">{enq.email}</td>
                                    <td data-label="Subject">{enq.subject}</td>
                                    <td data-label="Message">{enq.message.substring(0, 30)}...</td>
                                    <td data-label="Date">{new Date(enq.created_at).toLocaleDateString()}</td>
                                    <td data-label="Status">
                                        <span
                                            className={`badge ${enq.status === 'New'
                                                ? 'badge-new'
                                                : 'badge-read'
                                                }`}
                                        >
                                            {enq.status}
                                        </span>
                                    </td>
                                    <td data-label="Actions">
                                        <button
                                            style={{
                                                cursor: 'pointer',
                                                border: 'none',
                                                background: 'none',
                                                color: '#1a237e',
                                                fontWeight: '600'
                                            }}
                                            onClick={() => handleView(enq)}
                                        >
                                            View
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* ===== VIEW MODAL ===== */}
            {selectedEnquiry && (
                <div className="modal-overlay">
                    <div className="modal-card" style={{ position: 'relative' }}>
                        <button
                            className="close-cross-btn"
                            onClick={() => setSelectedEnquiry(null)}
                            style={{
                                position: 'absolute',
                                top: '15px',
                                right: '15px',
                                background: 'transparent',
                                border: 'none',
                                fontSize: '1.5rem',
                                cursor: 'pointer',
                                color: '#666'
                            }}
                        >
                            &times;
                        </button>
                        <h3>{selectedEnquiry.subject}</h3>

                        <p><strong>Name:</strong> {selectedEnquiry.name}</p>
                        <p><strong>Email:</strong> {selectedEnquiry.email}</p>
                        <p>
                            <strong>Date:</strong>{' '}
                            {new Date(selectedEnquiry.created_at).toLocaleString()}
                        </p>

                        {selectedEnquiry.organization_need && (
                            <p>
                                <strong>Organizational Need:</strong><br />
                                {selectedEnquiry.organization_need}
                            </p>
                        )}

                        <div className="modal-message">
                            <strong>Message:</strong>
                            <p>{selectedEnquiry.message}</p>
                        </div>

                        <button
                            className="close-btn"
                            onClick={() => setSelectedEnquiry(null)}
                        >
                            Close
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Enquiries;